import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';

import { StandardModal } from '../components/modal/Modal.jsx';
import { PrimaryButton, SecondaryButton } from '../components/button/Button.jsx';
import { Password } from '../components/form/Input.jsx';
import useAppStatus from '../shell/app-status/useAppStatus.js';
import { UserAuth } from './AuthContext.jsx';
import {
    auth, facebookAuthProvider, googleAuthProvider, microsoftAuthProvider, signInWithEmailAndPassword, signInWithPopup,
} from './Firebase.jsx';
import { completeReauth } from './sessionExpiry.js';
import { performSessionCleanup, safeRedirectToLogin } from './sessionCleanup.js';
import './SessionExpiredDialog.css';

const PROVIDERS = {
    'google.com': { label: 'Sign In with Google', provider: googleAuthProvider },
    'microsoft.com': { label: 'Sign In with Microsoft', provider: microsoftAuthProvider },
    'facebook.com': { label: 'Sign In with Facebook', provider: facebookAuthProvider },
};

/** What to tell the user when signing in again did not work. Empty: say nothing (they closed the popup). */
export const reauthErrorMessage = (error) => {
    switch (error?.code) {
        case 'auth/popup-closed-by-user':
        case 'auth/cancelled-popup-request':
            return '';
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
        case 'auth/invalid-login-credentials':
            return "That password isn't right. Try again.";
        case 'auth/too-many-requests':
            return 'Too many attempts. Wait a moment, then try again.';
        case 'auth/network-request-failed':
            return "We couldn't reach the sign-in service. Check your connection, then try again.";
        case 'auth/popup-blocked':
            return 'Your browser blocked the sign-in window. Allow pop-ups for this site, then try again.';
        case 'auth/user-disabled':
            return 'This account has been disabled. Go to the sign-in page to use another account.';
        default:
            return "We couldn't sign you in. Try again, or go to the sign-in page.";
    }
};

/**
 * Over the page when the session has expired and cannot be refreshed (sessionExpiry.js). The user signs in again
 * right here - with the same provider they used - and the page carries on, unsaved work included; requests that were
 * refused meanwhile go out again. Signing in as a different account, or choosing the sign-in page, ends this tab's
 * session cleanly and goes to sign-in with a link back to this page.
 */
const SessionExpiredDialog = () => {
    const { sessionExpired } = useAppStatus();
    const { user } = UserAuth() || {};
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    if (!sessionExpired) return null;

    const email = user?.email || '';
    const providerId = user?.providerData?.[0]?.providerId || (email ? 'password' : null);
    const provider = PROVIDERS[providerId];

    const goToSignIn = async () => {
        setBusy(true);
        await performSessionCleanup();
        safeRedirectToLogin();
    };

    const finish = async (credential) => {
        setPassword('');
        setError('');
        const uid = credential?.user?.uid;
        // Another account: this tab's data is the old user's, so nothing waiting may go out as the new one. Start
        // clean. (Checked against the page's user too, in case the wait did not know whose session it was.)
        if ((user?.uid && uid !== user.uid) || !completeReauth(uid)) {
            await goToSignIn();
        }
    };

    const attempt = async (signIn) => {
        setBusy(true);
        setError('');
        try {
            await finish(await signIn());
        } catch (err) {
            setError(reauthErrorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const signInWithPassword = () => attempt(() => signInWithEmailAndPassword(auth, email, password));
    const signInWithProvider = () => attempt(() => signInWithPopup(auth, provider.provider));

    const canUsePassword = providerId === 'password' && !!email;

    return (
        <StandardModal
            isOpen
            size="small"
            icon={<FontAwesomeIcon icon={faLock} />}
            title="Your Session Expired"
            subtitle="Sign in to continue. This page stays as it is, including anything you haven't saved."
            showCloseButton={false}
            onClose={() => {}}
            actions={(
                <>
                    <SecondaryButton onClick={goToSignIn} disabled={busy}>Go to Sign-In Page</SecondaryButton>
                    {canUsePassword && (
                        <PrimaryButton onClick={signInWithPassword} loading={busy} disabled={!password}>
                            Sign In
                        </PrimaryButton>
                    )}
                    {provider && (
                        <PrimaryButton onClick={signInWithProvider} loading={busy}>{provider.label}</PrimaryButton>
                    )}
                </>
            )}
        >
            {email && <p className="session-expired-account">Signed in as <strong>{email}</strong></p>}
            {canUsePassword && (
                <Password
                    label="Password"
                    value={password}
                    autoFocus
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && password && !busy) signInWithPassword(); }}
                />
            )}
            {!canUsePassword && !provider && (
                <p>Go to the sign-in page to sign in again. You'll come back to this page afterwards.</p>
            )}
            {error && <p className="session-expired-error" role="alert">{error}</p>}
        </StandardModal>
    );
};

export default SessionExpiredDialog;
